import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';
import clsx from 'clsx';
import Cookies from 'js-cookie';

import {
  LoadingType,
  PreQualificationStep,
  setAccount,
  setLoadingState,
  setPreQualificationStep,
  setSignInStep,
  SignInStep,
} from 'actions';
import { setBorrowerChangedInfoAfterProvePreFill } from 'actions/application';
import { setBorrower } from 'actions/borrower';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import Loader from 'components/Loader';
import { SearchLocationInput } from 'components/SearchLocationInput';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { AppRoutes } from 'routes/routes.enums';
import { updateBorrower } from 'thunks';
import {
  MASKED_SSN_LENGTH,
  BORROWER_FIRST_LAST_NAME_MAX_LENGTH,
  SSN_MAX_LENGTH,
  ZIP_CODE_MAX_LENGTH,
} from 'utils/constants';
import { identityFullStory } from 'utils/fullStory';
import {
  getStreetAddress,
  getStreetNameFromStreetAddress,
  getStreetNumberFromStreetAddress,
  maskSSN,
  removeNonDigits,
} from 'utils/stringUtils';
import { getMessageForInvalidFields } from 'utils/validationUtils';
import * as notificationManager from 'notifications/index';
import { FailureNotificationType } from 'notifications/index';
import Select from 'components/Select';
import { CurrentOwnershipStatus, PrequalificationVersion } from 'enums/ApplicationsVariables';
import { getOptions, OptionType } from 'utils/inputSelect';
import { DateFormat } from 'enums/DateFormat';
import {
  getDateFromFormat,
  isValidDateBeforeNow,
  removeNumberOfYears,
  isMonthYearNotBeforeMinYear,
} from 'utils/dateUtils';
import NumberInput from 'components/Inputs/NumberInput';
import {
  validateFirstName,
  validateLastName,
  validateCityField,
  removeIncorrectSymbols,
  getAddressCaptionMessage,
} from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { EyeIcon } from 'components/Icons';
import { BorrowerInformationFormValues } from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.types';
import { BorrowerInformationLabel } from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.enums';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { switchToPrequalificationV2 } from 'utils/switchToPrequalificationV2';
import { marketingApi } from 'api/MarketingApi';
import { isPuertoRicoZipCode } from 'utils/address';
import { ConceptualHqEvent } from 'enums/ConceptualHqEvent';
import { getConceptualDeviceId } from 'routes/routes.utils';

import styles from './BorrowerInformation.module.scss';

const YEAR_DIFFERENCE_FOR_PREVIOUS_ADDRESS = 2;

const BorrowerInformation = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const { isBorrowerAuthorized } = useAppSelector((state) => state.prequalificationPortal);
  const { application, borrower } = useAppSelector((store) => store);
  const [isAddressOfHomeImprovementOpened, setIsAddressOfHomeImprovementOpened] = useState(false);
  const [isPreviousAddressFormOpened, setIsPreviousAddressFormOpened] = useState(false);

  const {
    register,
    unregister,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    control,
    formState,
    getValues,
    handleSubmit,
    clearErrors,
  } = useForm<BorrowerInformationFormValues>({
    defaultValues: {
      borrowerFirstName: borrower.firstName,
      borrowerLastName: borrower.lastName,
      streetAddress: getStreetAddress(borrower.streetNumber, borrower.streetName),
      city: borrower.city,
      state: borrower.state,
      zipCode: borrower.zip,
      county: borrower.county || '',
      personalIdNumber: borrower.personalIdNumber,
      isHomeImprovementSameAsPrimaryAddress: borrower.isHomeImprovementSameAsPrimaryAddress,
      homeImprovementAddressStreetAddress: borrower.homeImprovementAddress?.street_name || '',
      homeImprovementAddressCity: borrower.homeImprovementAddress?.city || '',
      homeImprovementAddressState: borrower.homeImprovementAddress?.state_or_province || '',
      homeImprovementAddressZip: borrower.homeImprovementAddress?.zip_or_postal_code || '',
      ownershipStatus: borrower.ownershipStatus,
      moveInDate: borrower.moveInDate,
      previousAddressStreetAddress: borrower.previousAddress?.street_name || '',
      previousAddressCity: borrower.previousAddress?.city || '',
      previousAddressState: borrower.previousAddress?.state_or_province || '',
      previousAddressZip: borrower.previousAddress?.zip_or_postal_code || '',
      previousMoveInDate: borrower.previousMoveInDate,
    },
  });
  const watcher = watch();
  const [isSsnVisible, setSsnVisible] = useState(
    !!(!application![LoanInformationVariables.IsBorrowerManualFlow] || borrower.personalIdNumber),
  );

  const getErrorMessageForRequiredFields = (fieldLabel: string) => `${fieldLabel} is required`;

  const handleInputChange = () => {
    dispatch(setBorrowerChangedInfoAfterProvePreFill(true));
  };

  useEffect(() => {
    register('borrowerFirstName', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.FirstName),
      validate: (value) => {
        return validateFirstName(value, BorrowerInformationLabel.FirstName);
      },
    });
    register('borrowerLastName', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.LastName),
      validate: (value) => {
        return validateLastName(value, BorrowerInformationLabel.LastName);
      },
    });
    register('streetAddress', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeStreetAddress),
    });
    register('city', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.City),
      validate: (value) => {
        return validateCityField(value, BorrowerInformationLabel.City);
      },
    });
    register('state', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.State),
    });
    register('zipCode', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.ZipCode),
      validate: (value) => {
        const zipValue = value?.trim().replace(/\D/g, '');

        return zipValue.length === ZIP_CODE_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.ZipCode);
      },
    });
    register('personalIdNumber', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.SSN),
      validate: (value) => value.length === SSN_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.SSN),
    });
    register('ownershipStatus', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.OwnershipStatus),
    });
    register('moveInDate', {
      required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PurchaseDate),
      validate: (value) =>
        (isValidDateBeforeNow(value, DateFormat.MonthYear) && isMonthYearNotBeforeMinYear(value)) ||
        'Please enter a valid date',
    });
  }, [register, watcher]);

  useEffect(() => {
    if (watcher.isHomeImprovementSameAsPrimaryAddress === false) {
      register('homeImprovementAddressStreetAddress', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.WhereHomeImprovementsOccurring),
      });
      register('homeImprovementAddressCity', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementCity),
        validate: (value) => {
          return validateCityField(value, BorrowerInformationLabel.HomeImprovementCity);
        },
      });
      register('homeImprovementAddressState', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementState),
      });
      register('homeImprovementAddressZip', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.HomeImprovementZipCode),
        validate: (value) => {
          const zipValue = value?.trim().replace(/\D/g, '');

          return (
            zipValue?.length === ZIP_CODE_MAX_LENGTH ||
            getMessageForInvalidFields(BorrowerInformationLabel.HomeImprovementZipCode)
          );
        },
      });

      setIsAddressOfHomeImprovementOpened(true);
    } else {
      unregister([
        'homeImprovementAddressStreetAddress',
        'homeImprovementAddressCity',
        'homeImprovementAddressState',
        'homeImprovementAddressZip',
      ]);

      setIsAddressOfHomeImprovementOpened(false);
    }
  }, [watcher.isHomeImprovementSameAsPrimaryAddress]);

  useEffect(() => {
    if (
      isValidDateBeforeNow(watcher.moveInDate, DateFormat.MonthYear) &&
      getDateFromFormat(watcher.moveInDate, DateFormat.MonthYear) >
        removeNumberOfYears(YEAR_DIFFERENCE_FOR_PREVIOUS_ADDRESS)
    ) {
      register('previousAddressStreetAddress', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PreviousStreetAddress),
      });
      register('previousAddressCity', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PreviousCity),
        validate: (value) => {
          return validateCityField(value, BorrowerInformationLabel.PreviousCity);
        },
      });
      register('previousAddressState', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PreviousState),
      });
      register('previousAddressZip', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PreviousZip),
        validate: (value) => {
          const zipValue = value?.trim().replace(/\D/g, '');

          return (
            zipValue?.length === ZIP_CODE_MAX_LENGTH || getMessageForInvalidFields(BorrowerInformationLabel.PreviousZip)
          );
        },
      });
      register('previousMoveInDate', {
        required: getErrorMessageForRequiredFields(BorrowerInformationLabel.PreviousMoveInDate),
        validate: (value) =>
          (isValidDateBeforeNow(value, DateFormat.MonthYear) && isMonthYearNotBeforeMinYear(value)) ||
          'Please enter a valid date',
      });

      setIsPreviousAddressFormOpened(true);
    } else {
      unregister([
        'previousAddressStreetAddress',
        'previousAddressCity',
        'previousAddressState',
        'previousAddressZip',
        'previousMoveInDate',
      ]);

      setIsPreviousAddressFormOpened(false);
    }
  }, [watcher.moveInDate]);

  useEffect(() => {
    const getBorrowerInfo = async () => {
      if (!!application![LoanInformationVariables.IsBorrowerManualFlow]) {
        await dispatch(
          setBorrower({
            eligible: false,
          }),
        );
      }
    };

    getBorrowerInfo();

    const track = async () => {
      const deviceId = await getConceptualDeviceId();

      if (!deviceId) {
        return;
      }

      marketingApi
        .trackConceptualHqEvent({
          event: ConceptualHqEvent.BorrowerInformation,
          prequalificationVersion: PrequalificationVersion.First,
          borrowerId: borrower?.borrowerId,
          deviceId: deviceId,
        })
        .catch(() => {});
    };

    track();
  }, []);

  const goToNextStep = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIncome));
  };

  useEffect(() => {
    if (!watcher.state || watcher.state.length !== 2) {
      setValue('state', '');
    }
  }, []);

  const updateBorrowerData = async () => {
    await dispatch(
      updateBorrower({
        firstName: watcher.borrowerFirstName.trim(),
        lastName: watcher.borrowerLastName.trim(),
        address: {
          streetName: getStreetNameFromStreetAddress(watcher.streetAddress),
          streetNumber: getStreetNumberFromStreetAddress(watcher.streetAddress),
          city: watcher.city,
          stateOrProvince: watcher.state,
          zipOrPostalCode: watcher.zipCode,
          county: watcher.county,
        },
        dateOfBirth: borrower.dateOfBirth,
        phone: borrower.phone,
        email: borrower.email,
      }),
    ).unwrap();
  };

  const handleOnSubmit = handleSubmit(async () => {
    const result = await trigger();

    if (isPuertoRicoZipCode(watcher.zipCode) || isPuertoRicoZipCode(watcher.homeImprovementAddressZip)) {
      notificationManager.notifyFailure(FailureNotificationType.RestrictedZipCode);

      return;
    }

    if (result) {
      dispatch(setLoadingState({ type: LoadingType.GetData, loading: true }));

      const homeImprovementAddress = !watcher.isHomeImprovementSameAsPrimaryAddress
        ? {
            street_name: watcher.homeImprovementAddressStreetAddress,
            city: watcher.homeImprovementAddressCity,
            state_or_province: watcher.homeImprovementAddressState,
            zip_or_postal_code: watcher.homeImprovementAddressZip,
          }
        : {
            street_name: watcher.streetAddress,
            city: watcher.city,
            state_or_province: watcher.state,
            zip_or_postal_code: watcher.zipCode,
          };

      const previousAddress = {
        street_name: watcher.previousAddressStreetAddress || '',
        city: watcher.previousAddressCity || '',
        state_or_province: watcher.previousAddressState || '',
        zip_or_postal_code: watcher.previousAddressZip || '',
      };

      dispatch(
        setBorrower({
          firstName: watcher.borrowerFirstName.trim(),
          lastName: watcher.borrowerLastName.trim(),
          streetName: getStreetNameFromStreetAddress(watcher.streetAddress),
          streetNumber: getStreetNumberFromStreetAddress(watcher.streetAddress),
          city: watcher.city,
          state: watcher.state,
          zip: watcher.zipCode,
          county: watcher.county,
          personalIdNumber: watcher.personalIdNumber,
          isHomeImprovementSameAsPrimaryAddress: watcher.isHomeImprovementSameAsPrimaryAddress,
          homeImprovementAddress: homeImprovementAddress,
          previousAddress: previousAddress,
          ownershipStatus: watcher.ownershipStatus,
          moveInDate: watcher.moveInDate,
          previousMoveInDate: watcher.previousMoveInDate,
        }),
      );
      identityFullStory(borrower.email, `${watcher.borrowerFirstName}_${watcher.borrowerLastName}`);

      try {
        const borrowerShortInfo = await preQualificationApi.getBorrowerShortInfo({
          ssn: watcher.personalIdNumber,
        });

        if (isBorrowerAuthorized && borrowerShortInfo?.accountEmail) {
          if (borrowerShortInfo?.isEmailVerified) {
            await updateBorrowerData();
            goToNextStep();
          } else {
            navigator(AppRoutes.Login);
            dispatch(setSignInStep(SignInStep.EnterConfirmationCode));
          }

          return;
        }

        if (borrowerShortInfo.id) {
          await preQualificationApi.updateBorrowerMarketingConsent({
            email: borrower.email,
            fullConsentAgreed: borrower.fullConsentAgreed,
            consentAgreed: true,
            personalIdNumber: watcher.personalIdNumber,
          });
        }

        if (borrowerShortInfo.isPrequalificationV2RecoveryRequired) {
          dispatch(setAccount({ id: borrowerShortInfo.id }));
          dispatch(setSignInStep(SignInStep.PrequalOtpVerification));

          await switchToPrequalificationV2(AppRoutes.Login);

          return;
        }

        if (!borrowerShortInfo.id || (borrowerShortInfo.id && !borrowerShortInfo.accountEmail)) {
          dispatch(setSignInStep(SignInStep.CreateAccount));
          navigator(AppRoutes.Login);

          return;
        }

        if (borrowerShortInfo.id && borrowerShortInfo.accountEmail) {
          dispatch(setAccount({ email: borrowerShortInfo.accountEmail }));
          dispatch(setSignInStep(SignInStep.SignIn));
          navigator(AppRoutes.IdentityVerification);

          return;
        }
      } catch (error: any) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.GetData, loading: false }));
      }
    }
  });

  const handleBack = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.LoanInformation));
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <FormHeader title="Borrower Information" percent={22} />
          <FormBody>
            <div className={styles.rowContainer}>
              <Input
                label={BorrowerInformationLabel.FirstName}
                errorMessage={errors.borrowerFirstName?.message}
                className={styles.formInput}
                name="borrowerFirstName"
                onChange={(event) => {
                  handleInputChange();
                  setValue('borrowerFirstName', event.target.value);
                  trigger('borrowerFirstName');
                }}
                value={watcher.borrowerFirstName}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
                data-hj-allow
              />
              <Input
                label={BorrowerInformationLabel.LastName}
                errorMessage={errors.borrowerLastName?.message}
                className={styles.formInput}
                name="borrowerLastName"
                onChange={(event) => {
                  handleInputChange();
                  setValue('borrowerLastName', event.target.value);
                  trigger('borrowerLastName');
                }}
                value={watcher.borrowerLastName}
                maxLength={BORROWER_FIRST_LAST_NAME_MAX_LENGTH}
                data-hj-allow
              />
              <Input
                label={BorrowerInformationLabel.SSN}
                className={clsx(styles.formInput, styles.ssnInput)}
                name="personalIdNumber"
                type={isSsnVisible ? 'text' : 'password'}
                maxLength={isSsnVisible ? MASKED_SSN_LENGTH : SSN_MAX_LENGTH}
                onChange={(event) => {
                  const value = removeNonDigits(event.target.value.trim());

                  setValue('personalIdNumber', value);

                  if (formState.isSubmitted) {
                    trigger('personalIdNumber');
                  }
                }}
                errorMessage={errors.personalIdNumber?.message}
                value={
                  application![LoanInformationVariables.IsBorrowerManualFlow] && !borrower.personalIdNumber
                    ? maskSSN(watcher.personalIdNumber, !isSsnVisible)
                    : `***-**-${watcher.personalIdNumber.substr(5)}`
                }
                disabled={!application![LoanInformationVariables.IsBorrowerManualFlow] || !!borrower.personalIdNumber}
                inputMode="numeric"
                endAdornment={() =>
                  application![LoanInformationVariables.IsBorrowerManualFlow] && !borrower.personalIdNumber ? (
                    <EyeIcon
                      open={isSsnVisible}
                      className={styles.eyeIcon}
                      onClick={() => {
                        setSsnVisible(!isSsnVisible);
                      }}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            </div>
            <div className={styles.addressHeader}>Primary Address</div>
            <SearchLocationInput
              control={control}
              required
              useFormControl
              inputNames={{
                streetAddress: 'streetAddress',
                city: 'city',
                state: 'state',
                zip: 'zipCode',
                county: 'county',
              }}
              caption={getAddressCaptionMessage(BorrowerInformationLabel.HomeStreetAddress)}
              errors={{
                streetAddress: errors.streetAddress,
                city: errors.city,
                state: errors.state,
                zip: errors.zipCode,
              }}
              streetLabel={BorrowerInformationLabel.HomeStreetAddress}
              initialValue={{
                streetAddress: watcher.streetAddress,
                city: watcher.city,
                state: watcher.state,
                zip: watcher.zipCode,
              }}
              onStateChange={handleInputChange}
              inputChange={(name, value) => {
                clearErrors(name as keyof BorrowerInformationFormValues);
                handleInputChange();

                if (name === 'streetAddress') {
                  setValue(name as keyof BorrowerInformationFormValues, removeIncorrectSymbols(value));
                } else {
                  setValue(name as keyof BorrowerInformationFormValues, value);
                }

                if (formState.isSubmitted) {
                  trigger(name as keyof BorrowerInformationFormValues);
                }
              }}
              getValues={getValues}
            />
            <div className={styles.rowContainer}>
              <NumberInput
                label={BorrowerInformationLabel.PurchaseDate}
                placeholder="MM/YYYY"
                errorMessage={errors.moveInDate?.message}
                className={clsx(styles.inputDateContainer, styles.inputDateContainer)}
                name="moveInDate"
                onChange={(event) => {
                  clearErrors('moveInDate');
                  setValue('moveInDate', event.target.value);
                }}
                onBlur={() => {
                  if (watcher.moveInDate) {
                    trigger('moveInDate');
                  }
                }}
                format="##/####"
                value={watcher.moveInDate}
                mask="_"
                allowEmptyFormatting
              />
              <div className={styles.formInput}>
                <Select
                  useFormControl
                  required
                  control={control}
                  onChange={(event: any) => {
                    setValue('ownershipStatus', event.value as CurrentOwnershipStatus);
                    trigger('ownershipStatus');
                  }}
                  name="ownershipStatus"
                  label={BorrowerInformationLabel.OwnershipStatus}
                  options={getOptions(CurrentOwnershipStatus)}
                  value={getOptions(CurrentOwnershipStatus).find(
                    (option: OptionType) => watcher.ownershipStatus === option.value,
                  )}
                  errorMessage={errors.ownershipStatus?.message}
                />
              </div>
            </div>
            <div className={styles.addressHeader}>Address of Home Improvement</div>
            <div className={styles.radioButtonsHeader}>Same as primary address?</div>
            <FormControl>
              <Controller
                name="isHomeImprovementSameAsPrimaryAddress"
                control={control}
                rules={{
                  validate: (value) => {
                    return value !== null;
                  },
                }}
                render={() => (
                  <>
                    <RadioGroup
                      value={watcher.isHomeImprovementSameAsPrimaryAddress}
                      onChange={(event) => {
                        setValue('isHomeImprovementSameAsPrimaryAddress', JSON.parse(event.target.value));

                        if (isSubmitted) {
                          trigger();
                        }
                      }}
                    >
                      <FormControlLabel
                        classes={{ label: styles.radioButtonLabel }}
                        value={true}
                        control={
                          <Radio
                            classes={{
                              root: styles.radioButton,
                            }}
                          />
                        }
                        label={BorrowerInformationLabel.HomeImprovementYes}
                      />
                      <FormControlLabel
                        classes={{ label: styles.radioButtonLabel }}
                        value={false}
                        control={
                          <Radio
                            classes={{
                              root: styles.radioButton,
                            }}
                          />
                        }
                        label={BorrowerInformationLabel.HomeImprovementNo}
                      />
                    </RadioGroup>
                    {!!errors?.isHomeImprovementSameAsPrimaryAddress && (
                      <FormHelperText className={styles.errorMessage}>
                        {BorrowerInformationLabel.HomeImprovementInformationIsRequired}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>
            {isAddressOfHomeImprovementOpened && (
              <div className={styles.homeImprovementContainer}>
                <SearchLocationInput
                  control={control}
                  required
                  useFormControl
                  inputNames={{
                    streetAddress: 'homeImprovementAddressStreetAddress',
                    city: 'homeImprovementAddressCity',
                    state: 'homeImprovementAddressState',
                    zip: 'homeImprovementAddressZip',
                  }}
                  errors={{
                    streetAddress: errors.homeImprovementAddressStreetAddress,
                    city: errors.homeImprovementAddressCity,
                    state: errors.homeImprovementAddressState,
                    zip: errors.homeImprovementAddressZip,
                  }}
                  caption={getAddressCaptionMessage(BorrowerInformationLabel.WhereHomeImprovementsOccurring)}
                  streetLabel={BorrowerInformationLabel.WhereHomeImprovementsOccurring}
                  initialValue={{
                    streetAddress: watcher.homeImprovementAddressStreetAddress || '',
                    city: watcher.homeImprovementAddressCity || '',
                    state: watcher.homeImprovementAddressState || '',
                    zip: watcher.homeImprovementAddressZip || '',
                  }}
                  inputChange={(name, value) => {
                    clearErrors(name as keyof BorrowerInformationFormValues);

                    if (name === 'homeImprovementAddressStreetAddress') {
                      setValue(name as keyof BorrowerInformationFormValues, removeIncorrectSymbols(value));
                    } else {
                      setValue(name as keyof BorrowerInformationFormValues, value);
                    }

                    if (formState.isSubmitted) {
                      trigger(name as keyof BorrowerInformationFormValues);
                    }
                  }}
                  getValues={getValues}
                />
              </div>
            )}
            {isPreviousAddressFormOpened && (
              <div className={styles.previousAddressContainer}>
                <div className={styles.addressHeader}>Previous Address</div>
                <SearchLocationInput
                  control={control}
                  required
                  useFormControl
                  inputNames={{
                    streetAddress: 'previousAddressStreetAddress',
                    city: 'previousAddressCity',
                    state: 'previousAddressState',
                    zip: 'previousAddressZip',
                  }}
                  errors={{
                    streetAddress: errors.previousAddressStreetAddress,
                    city: errors.previousAddressCity,
                    state: errors.previousAddressState,
                    zip: errors.previousAddressZip,
                  }}
                  streetLabel={BorrowerInformationLabel.PreviousStreetAddress}
                  cityLabel={BorrowerInformationLabel.PreviousCity}
                  stateLabel={BorrowerInformationLabel.PreviousState}
                  zipLabel={BorrowerInformationLabel.PreviousZip}
                  caption={getAddressCaptionMessage(BorrowerInformationLabel.PreviousStreetAddress)}
                  initialValue={{
                    streetAddress: watcher.previousAddressStreetAddress || '',
                    city: watcher.previousAddressCity || '',
                    state: watcher.previousAddressState || '',
                    zip: watcher.previousAddressZip || '',
                  }}
                  inputChange={(name, value) => {
                    clearErrors(name as keyof BorrowerInformationFormValues);

                    if (name === 'previousAddressStreetAddress') {
                      setValue(name as keyof BorrowerInformationFormValues, removeIncorrectSymbols(value));
                    } else {
                      setValue(name as keyof BorrowerInformationFormValues, value);
                    }

                    if (formState.isSubmitted) {
                      trigger(name as keyof BorrowerInformationFormValues);
                    }
                  }}
                  getValues={getValues}
                />
                <div className={styles.rowContainer}>
                  <NumberInput
                    label={BorrowerInformationLabel.PreviousMoveInDate}
                    placeholder="MM/YYYY"
                    errorMessage={errors.previousMoveInDate?.message}
                    className={styles.formInput}
                    name="previousMoveInDate"
                    onChange={(event) => {
                      clearErrors('previousMoveInDate');
                      setValue('previousMoveInDate', event.target.value);
                    }}
                    onBlur={() => {
                      if (watcher.previousMoveInDate) {
                        trigger('previousMoveInDate');
                      }
                    }}
                    format="##/####"
                    value={watcher.previousMoveInDate}
                    mask="_"
                    allowEmptyFormatting
                  />
                </div>
              </div>
            )}
          </FormBody>
          <div className={styles.navigationButtons}>
            <Button className={styles.backButton} buttonType={ButtonType.Secondary} withArrow onClick={handleBack}>
              Back
            </Button>
            <Button onClick={handleOnSubmit} withArrow>
              Next
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default BorrowerInformation;
